// Settings → Dimension values (V-10): one section per Dimension the business's Business Tree has, by its label, with
// each value's name, code, the Counterparty it stands for and whether it is retired. Adding, renaming and retiring
// run the same commands as chat (#603), with the same refusals; only the buttons the person's Roles allow show.
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { useWords } from "../../i18n/words.js";
import { ListRow, changeProblem } from "../ListRow.js";
import type { DimensionValue, DimensionValues as Values, DimensionValuesApi } from "./api.js";

function AddValue({
  label,
  busy,
  onAdd,
}: {
  label: string;
  busy: boolean;
  onAdd: (name: string, code: string) => Promise<boolean>;
}) {
  const w = useWords();
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (await onAdd(name.trim(), code.trim())) {
      setName("");
      setCode("");
    }
  };
  return (
    <form className="card" aria-label={w.dimensionsAddTo(label)} onSubmit={(event) => void submit(event)}>
      <label className="field">
        {w.listName} <input value={name} maxLength={120} onChange={(event) => setName(event.target.value)} />
      </label>
      <label className="field">
        {w.dimensionsCode} <input value={code} maxLength={30} onChange={(event) => setCode(event.target.value)} />
      </label>
      <button type="submit" className="btn btn-go" disabled={busy || name.trim() === ""}>
        {w.listAdd}
      </button>
    </form>
  );
}

export function DimensionValues({ api }: { api: DimensionValuesApi }) {
  const w = useWords();
  const [shown, setShown] = useState<Values>();
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string>();
  const [problem, setProblem] = useState<string>();

  const load = useCallback(
    () =>
      api.list().then(
        (values) => setShown(values),
        (error: unknown) => setProblem(changeProblem(error, w)),
      ),
    [api, w],
  );
  useEffect(() => {
    void load();
  }, [load]);

  /** Runs a change, then shows the list as it is after it; false when it was refused. */
  const change = async (run: () => Promise<DimensionValue>, said: (value: DimensionValue) => string) => {
    setBusy(true);
    setNotice(undefined);
    setProblem(undefined);
    try {
      const done = said(await run());
      await load();
      setNotice(done);
      return true;
    } catch (error) {
      setProblem(changeProblem(error, w));
      return false;
    } finally {
      setBusy(false);
    }
  };

  if (!shown) return problem ? <p role="alert">{problem}</p> : <p role="status">{w.settingsLoading}</p>;
  const { may } = shown;
  const reads = !may.add && !may.rename && !may.retire;
  return (
    <>
      <p className="muted">{w.dimensionsHelp}</p>
      {reads ? <p className="muted">{w.listReadOnly}</p> : null}
      {notice ? <p role="status">{notice}</p> : null}
      {problem ? (
        <p className="alert" role="alert">
          {problem}
        </p>
      ) : null}
      {shown.dimensions.map((dimension) => {
        const headingId = `dimension-${dimension.id}`;
        return (
          <section key={dimension.id} className="section" aria-labelledby={headingId}>
            {/* The pack's own words, which the packs translate (#220). */}
            <h2 id={headingId} data-pack="">
              {dimension.label}
            </h2>
            {dimension.values.length === 0 ? <p className="muted">{w.dimensionsEmpty(dimension.label)}</p> : null}
            <ul className="queue">
              {dimension.values.map((value) => (
                <ListRow
                  key={value.id}
                  name={value.name}
                  details={[
                    value.code ? w.dimensionsCodeShown(value.code) : null,
                    value.counterparty
                      ? value.counterparty.name
                        ? w.dimensionsStandsFor(value.counterparty.name)
                        : w.dimensionsStandsForHidden
                      : null,
                  ]
                    .filter((d) => d !== null)
                    .join(" · ")}
                  retired={value.retired}
                  mayRename={may.rename}
                  mayRetire={may.retire}
                  busy={busy}
                  onRename={(name) =>
                    change(
                      () => api.rename(value.id, name),
                      (renamed) => w.listRenamed(value.name, renamed.name),
                    )
                  }
                  onRetire={() =>
                    change(
                      () => api.retire(value.id),
                      (retired) => w.listRetiredNotice(retired.name),
                    )
                  }
                />
              ))}
            </ul>
            {may.add ? (
              <AddValue
                label={dimension.label}
                busy={busy}
                onAdd={(name, code) =>
                  change(
                    () => api.add({ dimension: dimension.id, name, ...(code ? { code } : {}) }),
                    (added) => w.listAdded(added.name, dimension.label),
                  )
                }
              />
            ) : null}
          </section>
        );
      })}
    </>
  );
}
